import { Fragment, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvidedDragHandleProps,
} from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { MenuItem, MenuSection } from "src/business/restaurant/menu";
import { MenuItemRow } from "./MenuItemRow";
import { colorTextMuted, colorBorder, colorTextSecondary } from "src/design/tokens";
import { useTranslation } from "react-i18next";

interface Props {
  menu: MenuSection[];
  search: string;
  selectedCategories: string[];
  canUpdate: boolean;
  onEdit: (item: MenuItem, sectionName: string) => void;
  onDelete: (itemId: string | null) => void;
  onToggleFeatured: (item: MenuItem) => void;
  onReorder: (updates: { id: string; priority: number }[]) => Promise<void>;
}

const SectionDivider = ({
  label,
  dragHandleProps,
}: {
  label: string;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25, mt: 0.5 }}>
    <Box
      {...(dragHandleProps ?? {})}
      sx={{
        display: "flex",
        alignItems: "center",
        color: colorTextMuted,
        cursor: dragHandleProps ? "grab" : "default",
        opacity: dragHandleProps ? 1 : 0,
        pointerEvents: dragHandleProps ? "auto" : "none",
        width: 16,
        flexShrink: 0,
      }}
    >
      <DragIndicatorIcon sx={{ fontSize: 14 }} />
    </Box>
    <Box sx={{ flex: 1, height: "2px", backgroundColor: colorBorder, borderRadius: 1 }} />
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontWeight: 600,
        color: colorTextSecondary,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: "2px", backgroundColor: colorBorder, borderRadius: 1 }} />
  </Box>
);

export const MenuItemsList = ({
  menu,
  search,
  selectedCategories,
  canUpdate,
  onEdit,
  onDelete,
  onToggleFeatured,
  onReorder,
}: Props) => {
  const { t } = useTranslation();

  const [localSections, setLocalSections] = useState<MenuSection[]>(menu);
  useEffect(() => setLocalSections(menu), [menu]);

  const canDrag = !search && canUpdate;
  const visibleSections = selectedCategories.length
    ? localSections.filter((s) => selectedCategories.includes(s.section))
    : localSections;
  const canDragSections = canDrag && selectedCategories.length === 0 && visibleSections.length > 1;

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;

    const sourceIndx = source.index

    if (!destination) return;

    const destinationIndx = destination?.index

    if (source.droppableId === destination.droppableId && sourceIndx === destinationIndx) return;

    const startIndex = Math.min(sourceIndx, destinationIndx)
    const endIndex = Math.max(sourceIndx, destinationIndx)

    if (type === "SECTION") {
      const reordered = [...localSections];
      const [removed] = reordered.splice(sourceIndx, 1);
      reordered.splice(destinationIndx, 0, removed);
      setLocalSections(reordered);

      let priority = 0;
      const updates: { id: string; priority: number }[] = [];
      for (const section of reordered) {
        for (const item of section.menuItems) {
          if (item.id) updates.push({ id: item.id, priority: priority++ });
        }
      }
      if (updates.length > 0) onReorder(updates);
      return;
    }

    if (type === "ITEM" && source.droppableId === destination.droppableId) {
      const sectionName = source.droppableId;
      const sectionIdx = localSections.findIndex((s) => s.section === sectionName);
      if (sectionIdx === -1) return;

      const menuItems = localSections[sectionIdx].menuItems

      const sectionItems = menuItems.slice(startIndex, endIndex + 1)
      const sourceItem = sectionItems[sourceIndx - destinationIndx];
      const destinationItem = sectionItems[0]

      const reorderdSubsection = sectionItems.map((item) => {
        if (item.id === sourceItem.id) return { ...item, priority: destinationItem.priority }
        return { ...item, priority: item.priority + 1 }
      }).sort((a, b) => a.priority - b.priority)

      const fullReorderedSection = menuItems.slice(0, startIndex).concat(reorderdSubsection).concat(menuItems.slice(endIndex + 1))


      const newSections = [...localSections];
      newSections[sectionIdx] = { ...newSections[sectionIdx], menuItems: fullReorderedSection };
      setLocalSections(newSections);

      const updates = reorderdSubsection
        .map((item) => ({ id: item.id as string, priority: item.priority }));

      if (updates.length > 0) onReorder(updates);
    }
  };

  // Static rendering when searching — DnD indices would be unreliable on filtered results
  if (search) {
    const searchResults = visibleSections.flatMap((s) =>
      s.menuItems
        .filter((item) => {
          const q = search.toLowerCase();
          return (
            item.name.toLowerCase().includes(q) ||
            (item.description ?? "").toLowerCase().includes(q)
          );
        })
        .map((item) => ({ item, sectionName: s.section }))
    );

    if (searchResults.length === 0) {
      return (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography sx={{ color: colorTextMuted, fontSize: "0.875rem" }}>
            {t("menu.empty")}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {searchResults.map(({ item, sectionName }, idx) => {
          const showDivider = idx === 0 || searchResults[idx - 1].sectionName !== sectionName;
          const isLastInSection =
            idx === searchResults.length - 1 ||
            searchResults[idx + 1].sectionName !== sectionName;
          return (
            <Fragment key={item.id ?? `unsaved-${idx}`}>
              {showDivider && <SectionDivider label={sectionName} />}
              <MenuItemRow
                item={item}
                sectionName={sectionName}
                isLastInSection={isLastInSection}
                isDragging={false}
                canDrag={false}
                canUpdate={canUpdate}
                dragHandleProps={null}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleFeatured={onToggleFeatured}
              />
            </Fragment>
          );
        })}
      </Box>
    );
  }

  const totalItems = visibleSections.reduce((sum, s) => sum + s.menuItems.length, 0);
  if (totalItems === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography sx={{ color: colorTextMuted, fontSize: "0.875rem" }}>
          {t("menu.empty")}
        </Typography>
      </Box>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="sections" type="SECTION" isDropDisabled={!canDragSections}>
        {(sectionDroppable) => (
          <Box ref={sectionDroppable.innerRef} {...sectionDroppable.droppableProps}>
            {visibleSections.map((section, sectionIdx) => (
              <Draggable
                key={section.section}
                draggableId={`section::${section.section}`}
                index={sectionIdx}
                isDragDisabled={!canDragSections}
              >
                {(sectionDraggable, sectionSnapshot) => (
                  <Box
                    ref={sectionDraggable.innerRef}
                    {...sectionDraggable.draggableProps}
                    sx={{
                      opacity: sectionSnapshot.isDragging ? 0.85 : 1,
                      backgroundColor: sectionSnapshot.isDragging ? `${colorBorder}40` : "transparent",
                      borderRadius: sectionSnapshot.isDragging ? "8px" : 0,
                    }}
                  >
                    <SectionDivider
                      label={section.section}
                      dragHandleProps={canDragSections ? sectionDraggable.dragHandleProps : null}
                    />
                    <Droppable droppableId={section.section} type="ITEM" >
                      {(itemDroppable) => (
                        <Box ref={itemDroppable.innerRef} {...itemDroppable.droppableProps}>
                          {section.menuItems.map((item, itemIdx) => {
                            const isLastInSection = itemIdx === section.menuItems.length - 1;
                            return (
                              <Draggable
                                key={item.id ?? `unsaved-${itemIdx}`}
                                draggableId={item.id ?? `unsaved-${sectionIdx}-${itemIdx}`}
                                index={itemIdx}
                                isDragDisabled={!canDrag}
                              >
                                {(itemDraggable, itemSnapshot) => (
                                  <Box
                                    ref={itemDraggable.innerRef}
                                    {...itemDraggable.draggableProps}
                                  >
                                    <MenuItemRow
                                      item={item}
                                      sectionName={section.section}
                                      isLastInSection={isLastInSection}
                                      isDragging={itemSnapshot.isDragging}
                                      canDrag={canDrag}
                                      canUpdate={canUpdate}
                                      dragHandleProps={itemDraggable.dragHandleProps}
                                      onEdit={onEdit}
                                      onDelete={onDelete}
                                      onToggleFeatured={onToggleFeatured}
                                    />
                                  </Box>
                                )}
                              </Draggable>
                            );
                          })}
                          {itemDroppable.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  </Box>
                )}
              </Draggable>
            ))}
            {sectionDroppable.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};
